import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "api/api";
import endpoint from "api/endpoints";

/**
 * Fetch once, keep across route changes, refetch after STALE_MS — NetSuite is
 * slow enough that avoiding a re-ask beats freshness here.
 */
const STALE_MS = 5 * 60 * 1000;

const PAGE_SIZE = 50;

const initialState = {
  items: [],
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  hasMore: false,
  offset: 0,
  lastFetch: null,
  generation: 0,
  authFailed: false,
};

export const fetchOrderAgain = createAsyncThunk(
  "orderAgain/fetch",
  async (
    { userId, getAccessTokenSilently, offset = 0, limit = PAGE_SIZE },
    { getState, rejectWithValue }
  ) => {
    // Captured before the request goes out so a clearOrderAgain that lands
    // while this is in flight can be detected when the response comes back.
    const generation = getState().orderAgain.generation;
    try {
      const token = await getAccessTokenSilently();
      const url = endpoint.GET_ORDER_AGAIN({ userId, limit, offset });
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data || {};
      return {
        items: Array.isArray(data.items) ? data.items : [],
        hasMore: Boolean(data.hasMore),
        offset,
        generation,
      };
    } catch (err) {
      const status = err?.response?.status;
      return rejectWithValue({
        message:
          err?.response?.data?.error || "Failed to load your previous orders",
        authFailed: status === 401 || status === 403,
      });
    }
  }
);

const orderAgainSlice = createSlice({
  name: "orderAgain",
  initialState,
  reducers: {
    clearOrderAgain: (state) => ({
      ...initialState,
      generation: state.generation + 1,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderAgain.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOrderAgain.fulfilled, (state, action) => {
        const { items, hasMore, offset, generation } = action.payload;
        // Ignore responses from before the last clearOrderAgain, so a "load
        // more" in flight during a clear can't append onto the reset list.
        if (generation !== state.generation) return;
        state.items = offset === 0 ? items : [...state.items, ...items];
        state.hasMore = hasMore;
        state.offset = offset;
        state.status = "succeeded";
        state.error = null;
        state.lastFetch = Date.now();
      })
      .addCase(fetchOrderAgain.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload?.message || "Failed to load your previous orders";
        state.authFailed = Boolean(action.payload?.authFailed);
      });
  },
});

export const { clearOrderAgain } = orderAgainSlice.actions;

export const selectOrderAgainItems = (state) => state.orderAgain.items;
export const selectOrderAgainStatus = (state) => state.orderAgain.status;
export const selectOrderAgainError = (state) => state.orderAgain.error;
export const selectOrderAgainHasMore = (state) => state.orderAgain.hasMore;
export const selectOrderAgainOffset = (state) => state.orderAgain.offset;

export const selectShouldFetchOrderAgain = (state) => {
  const { lastFetch, status, authFailed } = state.orderAgain;
  if (authFailed || status === "loading") return false;
  if (!lastFetch) return true;
  return Date.now() - lastFetch > STALE_MS;
};

export const ORDER_AGAIN_PAGE_SIZE = PAGE_SIZE;

export default orderAgainSlice.reducer;
